import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Page, PageHeader } from '@tanium/react-page';
import { Well } from '@tanium/react-well';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { Form, FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { Select } from '@tanium/react-select';
import { TooltippedButton } from '@tanium/react-button';
import { Spinner } from '@tanium/react-spinner';
import { CircleCheckIcon } from '@tanium/react-graphics';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { Notification } from '@tanium/react-persistent-notifications';
import { useDebouncedCallback } from 'use-debounce';
import { LinkText } from '../../components/LinkText';
import { DataRegions, DocLinks } from '../../utilities/constants';
import { getDomainSuffix } from '../../utilities/instanceHelper';
import { HeaderBoldText, MediumSubtleText, LargeBoldText, MediumBoldText } from 'TextStyles';
import { useAuthenticatedApiContext } from '../../api/useApiClient';
import { ApiError } from '../../hooks/useApiErrorMsgParser';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useTenant } from '../../hooks/useTenant';

const TaniumSelect = Select as unknown as React.ComponentType<any>;
type DataRegionSelectOption = { label: string; value: string };
type DomainValidationResult = { isUnique: boolean };

const LICENSE_SUMMARY_TRANSLATION_KEYS: Record<string, readonly string[]> = {
  'Tanium Core Plus': [
    'Endpoint visibility',
    'Control',
    'Data collection',
    'Industry benchmarking',
    'Certificate lifecycle management',
    'Secure privileged access',
  ],
  'Endpoint Management Plus': [
    'Deploy, patch, and uninstall software',
    'Ensure devices remain compliant, secure and operational.',
    'Automated deployment and configuration,',
    'Continuous policy enforcement',
  ],
  'Exposure Management': ['Attack surface visibility', 'Identify, prioritize and remediate vulnerabilities and risk'],
  'Security Operations': ['Detect, react and recover quickly from attacks', 'Accelerate incident response'],
  'Operational Technology': ['Discover and inventory OT devices across multiple OT protocols'],
  'AI Usage': ['Advanced: <strong>100 credits per month</strong>'],
};

const VANITY_URL_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export const CreateTaniumInstancePage: React.VFC = () => {
  const { t } = useTranslation();
  const domainSuffix = getDomainSuffix();

  const dataRegionOptions: DataRegionSelectOption[] = useMemo(
    () => [
      { label: t('United States'), value: DataRegions.US_C1 },
      { label: t('Canada'), value: DataRegions.CA_C1 },
      { label: t('European Union'), value: DataRegions.EU_N1 },
    ],
    [t]
  );

  const [customerName, setCustomerName] = useState('');
  const [vanityUrl, setVanityUrl] = useState('');
  const [vanityUrlError, setVanityUrlError] = useState('');
  const [isValidatingDomain, setIsValidatingDomain] = useState(false);
  const [isDomainAvailable, setIsDomainAvailable] = useState(false);
  const [selectedDataRegion, setSelectedDataRegion] = useState<DataRegionSelectOption>(dataRegionOptions[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiClient = useAuthenticatedApiContext();
  const { showNotification } = useToastNotificationsControl();
  const { loadLinkedTaniumInstances, linkedTaniumInstances } = useTaniumInstances();
  const { tenant } = useTenant();

  const activeInstanceCount = linkedTaniumInstances.filter((i) => !i.deletedAt).length;
  const instanceLimit = tenant?.mspInstanceLimit || 10;
  const isAtLimit = activeInstanceCount >= instanceLimit;

  const formInitialValue = useMemo(() => ({ customerName, vanityUrl }), [customerName, vanityUrl]);

  const validateVanityUrl = useCallback(
    (value: string) => {
      setIsDomainAvailable(false);
      if (!value) {
        setVanityUrlError('');
        return;
      }
      // Clear errors immediately when input looks valid so far
      if (value.length >= 3 && VANITY_URL_REGEX.test(value)) {
        setVanityUrlError('');
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      debouncedVanityCheck.callback(value);
    },
    [t]
  );

  const debouncedVanityCheck = useDebouncedCallback(async (value: string) => {
    if (value.length < 3) {
      setVanityUrlError(t('Domain must be at least 3 characters long'));
      return;
    }
    if (!VANITY_URL_REGEX.test(value)) {
      setVanityUrlError(
        t('Domain can only contain lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen')
      );
      return;
    }
    setIsValidatingDomain(true);
    try {
      const result = (await apiClient.validateDomain(`${value}${domainSuffix}`)) as DomainValidationResult;
      if (result.isUnique) {
        setIsDomainAvailable(true);
      } else {
        setVanityUrlError(t('This domain is already in use'));
      }
    } catch {
      setVanityUrlError(t('Failed to validate domain'));
    } finally {
      setIsValidatingDomain(false);
    }
  }, 500);

  const isFormValid = !isAtLimit && customerName.trim() !== '' && vanityUrl.trim() !== '' && !vanityUrlError && !isValidatingDomain;

  const handleCreate = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await apiClient.createMspInstance({
        customerName: customerName.trim(),
        domain: `${vanityUrl.trim()}${domainSuffix}`,
        cloudRegion: selectedDataRegion.value,
      });
      showNotification({
        type: 'success',
        contents: t('Instance creation started'),
      });
      await loadLinkedTaniumInstances.execute();
      window.location.href = '#/';
    } catch (error) {
      const apiError = error as ApiError;
      const isDomainInUse = apiError.displayMessage?.includes('domain is already in use');
      const isLimitExceeded = apiError.response?.status === 429;
      showNotification({
        type: 'error',
        contents: isDomainInUse
          ? t('This domain is already in use. Please choose a different vanity URL.')
          : isLimitExceeded
          ? t('You have reached your MSP instance limit. Please contact Tanium to increase your limit.')
          : t('Failed to create instance'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [t, showNotification, apiClient, customerName, vanityUrl, domainSuffix, selectedDataRegion, loadLinkedTaniumInstances]);

  const handleCancel = useCallback(() => {
    window.location.href = '#/';
  }, []);

  return (
    <Page header={<PageHeader heading={''} />}>
      <Box p={2} style={{ maxWidth: 960, margin: '0 auto' }}>
        <HeaderBoldText>{t('Create Tanium Instance')}</HeaderBoldText>
        <Box mt={1}>
          <MediumSubtleText>{t('Create a Tanium instance to manage organizations you support.')}</MediumSubtleText>
        </Box>

        {isAtLimit && (
          <Box mt={2}>
            <Notification role="status" type="error">
              {t(
                'You have reached your MSP instance limit of {{limit}}. Please contact Tanium to increase your limit.',
                { limit: instanceLimit }
              )}
            </Notification>
          </Box>
        )}
        <Well mt={2} p={2}>
          <Form initialValue={formInitialValue} buttons={<></>} onValueChange={Function.prototype as () => void}>
            <FormRow dataKey="customerName" label={t('Customer Name')} required>
              <div style={{ display: 'flex' }}>
                <FormTextInput
                  value={customerName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomerName(e.target.value);
                  }}
                  style={{ flex: 1 }}
                />
              </div>
            </FormRow>
            <FormRow dataKey="vanityUrl" label={t('Vanity URL')} required errors={vanityUrlError}>
              <FormTextInput
                width="100%"
                value={vanityUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value.toLowerCase();
                  setVanityUrl(val);
                  validateVanityUrl(val);
                }}
                unitLabel={domainSuffix}
                unitLabelType="suffix"
              />
              {isValidatingDomain && (
                <Box mt={1}>
                  <Stack direction="row" alignItems="center" itemSpacing={1}>
                    <Spinner size={14} fill={false} />
                    <MediumSubtleText>{t('Checking availability...')}</MediumSubtleText>
                  </Stack>
                </Box>
              )}
              {isDomainAvailable && !vanityUrlError && !isValidatingDomain && (
                <Box mt={1} data-testid="domain-available">
                  <Stack direction="row" alignItems="center" itemSpacing={1}>
                    <CircleCheckIcon height={14} width={14} color="green" />
                    <span style={{ color: 'green', fontSize: 14 }}>{t('Domain is available')}</span>
                  </Stack>
                </Box>
              )}
            </FormRow>
            <FormRow dataKey="dataHostingRegion" label={t('Data Hosting Region')} required>
              <TaniumSelect
                options={dataRegionOptions}
                isMulti={false}
                value={selectedDataRegion}
                styles={{
                  container: (base: Record<string, unknown>) => ({
                    ...base,
                    width: '100%',
                  }),
                }}
                onChange={(option: DataRegionSelectOption) => {
                  setSelectedDataRegion(option);
                }}
              />
            </FormRow>
          </Form>
          <Box mt={1}>
            <MediumSubtleText>
              {t('Review the ')}
              <LinkText to={DocLinks.TANIUM_TRUST_CENTER}>{t('Trust Center documentation')}</LinkText>
              {t(' to learn how data is managed per region.')}
            </MediumSubtleText>
          </Box>
        </Well>

        <Well mt={2} p={2}>
          <LargeBoldText>{t('Account License Summary')}</LargeBoldText>
          <Box mt={1}>
            <MediumSubtleText>{t('The following solutions are configured for the Tanium account.')}</MediumSubtleText>
          </Box>
          {Object.entries(LICENSE_SUMMARY_TRANSLATION_KEYS).map(([solutionKey, featureKeys]) => (
            <Box key={solutionKey} mt={2}>
              <MediumBoldText>{t(solutionKey)}</MediumBoldText>
              <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                {featureKeys.map((featureKey) => (
                  <li key={featureKey} style={{ fontSize: 14, marginBottom: 4 }}>
                    <Trans i18nKey={featureKey} components={{ strong: <strong /> }}>
                      {featureKey}
                    </Trans>
                  </li>
                ))}
              </ul>
            </Box>
          ))}
        </Well>

        <Stack mt={2} itemSpacing={2}>
          <TooltippedButton variant="primary" disabled={!isFormValid || isSubmitting} onClick={handleCreate}>
            <Box as={Stack} justifyContent="center">
              {isSubmitting && (
                <Box mr={1}>
                  <Spinner size={16} fill={false} />
                </Box>
              )}
              {t('Create Instance')}
            </Box>
          </TooltippedButton>
          <TooltippedButton variant="secondary" onClick={handleCancel}>
            {t('Cancel')}
          </TooltippedButton>
        </Stack>
      </Box>
    </Page>
  );
};
